import {ICorrespondence, ICorrespondenceResponse} from "../interfaces/Correspondence";
import { IParamResponseTime } from "../redux/types/types";
import {getCorrespondenceAnswers} from "../services/firebase/correspondence";
import {calculateTrafficLightPercentage} from "../components/correspondence-module/pqrs/TrafficLight";
import {CommunicationChannelsList, CorrespondenceInputType} from "../enums/enums";
import { IntlShape } from "react-intl";
import moment from "moment";

/**
 * Determina el color del semáforo en función del tiempo de respuesta disponible.
 *
 * @param correspondence - Datos de la correspondencia a evaluar.
 * @param paramResponseTime - Parámetros de tiempo de respuesta.
 * @param paramsProcedure - Parámetros adicionales relacionados con los procedimientos.
 * @returns Un string que representa el color del semáforo o "No Aplica" si no aplica.
 */
export const getTrafficLightColor = (
    correspondence: ICorrespondence,
    paramResponseTime: IParamResponseTime,
    paramsProcedure: any
): string => {
    const validInputTypes = [CorrespondenceInputType.ER, CorrespondenceInputType.IR, CorrespondenceInputType.OR];
    if (!validInputTypes.includes(correspondence.inputType)) {
        return "No Aplica";
    }

    if (!correspondence.createAt) {
        return "No Aplica";
    }

    const { percentage, typeDays } = calculateTrafficLightPercentage(
        correspondence,
        correspondence.communicationType,
        paramResponseTime,
        paramsProcedure
    );

    if (percentage === null || isNaN(percentage) || !typeDays) {
        return "No Aplica";
    }

    if (percentage <= 60) return "Verde";
    if (percentage > 60 && percentage <= 70) return "Amarillo";
    if (percentage > 70 && percentage < 100) return "Naranja";
    if (percentage >= 100) return "Rojo";

    return "No Aplica";
};

/**
 * Verifica si una comunicación tiene documentos adjuntos.
 *
 * @param communication - Comunicación a evaluar.
 * @returns "Sí" si tiene documentos adjuntos, de lo contrario "No".
 */
export const getAttachmentStatus = (communication: ICorrespondence): string => {
    const hasDigitalSupport = communication.digitalSupport && communication.digitalSupport.length > 0;
    const hasAppendDocs = communication.appendDocs && communication.appendDocs.length > 0;
    return hasDigitalSupport || hasAppendDocs ? "Sí" : "No";
};

/**
 * Determina si una comunicación se envió por correo electrónico.
 *
 * @param communication - Comunicación a evaluar.
 * @returns Una promesa que resuelve con "Sí", "No", o "No Aplica" según corresponda.
 */
export const getEmailStatus = (communication: ICorrespondence): Promise<string> => {
    return new Promise((resolve) => {
        if (communication.inputType !== "EP") {
            resolve("No Aplica");
            return;
        }

        getCorrespondenceAnswers(
            communication.idEntity,
            communication.id,
            (responses: ICorrespondenceResponse[] | null) => {
                if (responses && responses.some(resp => resp.subject.startsWith("Enviado al"))) {
                    resolve("Sí");
                } else {
                    resolve("No");
                }
            }
        );
    });
};

/**
 * Obtiene el estado de respuesta de la comunicación traducido al idioma correspondiente.
 *
 * @param communication - Comunicación a evaluar.
 * @param intl - Objeto de internacionalización para traducción.
 * @returns El estado traducido o "Desconocido" si no hay estado definido.
 */
export const getResponseStatus = (communication: ICorrespondence, intl: any): string => {
    const status = communication.internalState
        ? intl.formatMessage({ id: communication.internalState })
        : "Desconocido";

    if (communication.inputType === "EP") {
        return "No Necesita Respuesta";
    }

    return status;
};

/**
 * Crea un mapa de oficinas de producción a partir de una lista.
 *
 * @param productionOffices - Lista de oficinas de producción.
 * @returns Un mapa con el ID de la oficina como clave y el nombre como valor.
 */
export const createOfficeMap = (productionOffices: any[]): Map<string, string> => {
    return new Map(productionOffices.map(office => [office.idProductionOffice, office.name]));
};

/**
 * Obtiene el nombre de una oficina de producción desde un mapa precargado.
 *
 * @param idProductionOffice - ID de la oficina de producción.
 * @param officeMap - Mapa de oficinas de producción.
 * @returns El nombre de la oficina o "Desconocido" si no se encuentra.
 */
export const getOfficeName = (idProductionOffice: string, officeMap: Map<string, string>): string => {
    return officeMap.get(idProductionOffice) || "Desconocido";
};

/**
 * Obtiene el nombre del canal de comunicación basado en el prefijo del ID.
 *
 * @param id - ID del canal.
 * @returns El nombre del canal o "Desconocido" si no se encuentra.
 */
export const getChannelName = (id: string, intl: IntlShape): string => {
    if (!id || id.length < 2) return "Desconocido";
    const channelPrefix = id.slice(0, 2);
    const channel = CommunicationChannelsList.find(ch => ch.value.startsWith(channelPrefix));
    return channel ? intl.formatMessage({ id: channel.name }) : "Desconocido";
};

/**
 * Convierte un texto a formato "Camel Case".
 *
 * @param str - Texto a convertir.
 * @returns El texto en formato "Camel Case" o una cadena vacía si el valor es nulo o indefinido.
 */
export const toCamelCase = (str: string | undefined | null): string => {
    if (!str) return "";
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Relación entre códigos de entrada y sus descripciones legibles.
 */
export const inputTypeMap: Record<string, string> = {
    "EP": "Externa Producida",
    "ER": "Externa Recibida",
    "IR": "Interna Recibida",
    "IPR": "Interna Recibida + Interna Producida",
    "IP": "Interna Producida",
    "OR": "Otros Envíos Recibidos",
    "OP": "Otros Envíos Producidos"
};

/**
 * Genera los parámetros relacionados con procedimientos a partir de los datos de tiempo de respuesta.
 *
 * @param paramResponseTime - Parámetros de tiempo de respuesta.
 * @returns Una lista de objetos que representan los procedimientos y sus tipos de días.
 */
export const createParamsProcedure = (paramResponseTime: IParamResponseTime): any[] => {
    return Object.keys(paramResponseTime)
        .filter((key) => key.endsWith("TypeDays"))
        .map((key) => {
            const procedureName = key.replace("TypeDays", "");
            return {
                procedureName,
                typeDays: paramResponseTime[key as keyof IParamResponseTime],
            };
        });
};

/**
 * Calcula la fecha límite de respuesta y los días restantes para una correspondencia.
 *
 * @param correspondence - Datos de la correspondencia.
 * @param paramResponseTime - Parámetros de tiempo de respuesta.
 * @returns Un objeto con la fecha de respuesta (`responseDateParam`) y los días restantes (`availableDays`).
 */
export const calculateResponseDate = (
    correspondence: ICorrespondence,
    paramResponseTime: IParamResponseTime
): { responseDateParam: moment.Moment | null; availableDays: number } => {
    const dateObj = correspondence.createAt?.seconds
        ? new Date(correspondence.createAt.seconds * 1000)
        : null;

    if (!dateObj) return { responseDateParam: null, availableDays: 0 };

    const paramNameDays = correspondence.communicationType?.option + "Days";
    const paramNameTypeDays = correspondence.communicationType?.option + "TypeDays";

    let daysAvailableDocument: any = correspondence.responseTimeDays
        ? correspondence.responseTimeDays
        : paramResponseTime[paramNameDays as keyof IParamResponseTime];

    const typeDays = paramResponseTime[paramNameTypeDays as keyof IParamResponseTime];

    const responseDateParam =
        typeDays === "BUSINESSDAYS"
            ? moment(dateObj).businessAdd(daysAvailableDocument, "days")
            : moment(dateObj).add(daysAvailableDocument, "days");

    let availableDays = responseDateParam.diff(moment(), "days");
    availableDays = availableDays > 0 ? availableDays : 0;

    return { responseDateParam, availableDays };
};

/**
 * Formatea el tipo de comunicación para su visualización.
 *
 * @param communicationType - Tipo de comunicación.
 * @param intl - Objeto de internacionalización para traducción.
 * @returns El tipo de comunicación formateado.
 */
export const formatCommunicationType = (communicationType: any, intl: any): string => {
    return communicationType.responseTime === 0 || !communicationType.responseTime
        ? intl.formatMessage({ id: communicationType.option })
        : communicationType.option;
};

/**
 * Verifica si un tipo de correspondencia no requiere respuesta o cálculo de fechas.
 *
 * @param inputType - Tipo de entrada de la correspondencia.
 * @returns `true` si es un tipo que no requiere cálculo de fechas, de lo contrario `false`.
 */
export const isExcludedInputType = (inputType: CorrespondenceInputType): boolean => {
    return inputType === CorrespondenceInputType.EP ||
        inputType === CorrespondenceInputType.IR ||
        inputType === CorrespondenceInputType.IP;
};

/**
 * Formatea el radicado de respuesta en el mismo formato que la tabla.
 *
 * @param dataAnswer - Objeto que contiene la información de la respuesta.
 * @returns Una cadena con el ID del radicado y la fecha formateada, o una cadena vacía si no hay respuesta.
 */
export const formatSettledResponse = (dataAnswer: any): string => {
    if (!dataAnswer) return " - ";

    return `${dataAnswer.id} - ${moment(new Date(dataAnswer.createAt.seconds * 1000)).format("YYYY/MM/DD - HH:mm:ss")}`;
};
